import { useState, useCallback } from "react";
import { httpsCallable } from "firebase/functions";
import { functions } from "../firebase";

/**
 * Custom hook for Stripe payment operations
 * Handles:
 * - Creating/updating payment methods
 * - Creating subscriptions
 * - Managing payment intents
 * - Handling payment confirmations
 */
export function useStripe() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState(null);

  /**
   * Create a setup intent for collecting payment method
   */
  const createSetupIntent = useCallback(async (email, name) => {
    setLoading(true);
    setError(null);
    try {
      const createSetupIntentFn = httpsCallable(functions, "createSetupIntent");
      const result = await createSetupIntentFn({
        email,
        name
      });

      return result.data;
    } catch (err) {
      const errorMessage = err.message || "Failed to create setup intent";
      setError(errorMessage);
      console.error("useStripe error (createSetupIntent):", err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Save payment method after successful setup
   */
  const savePaymentMethod = useCallback(async (setupIntentId, customerId, userInfo) => {
    setLoading(true);
    setError(null);
    try {
      const savePaymentMethodFn = httpsCallable(functions, "savePaymentMethod");
      const result = await savePaymentMethodFn({
        setupIntentId,
        customerId,
        userInfo
      });

      return result.data;
    } catch (err) {
      const errorMessage = err.message || "Failed to save payment method";
      setError(errorMessage);
      console.error("useStripe error (savePaymentMethod):", err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Create a subscription
   */
  const createSubscription = useCallback(async (planLevel, paymentMethodId) => {
    setLoading(true);
    setError(null);
    setPaymentStatus("processing");
    try {
      const createSubscriptionFn = httpsCallable(functions, "createSubscription");
      const result = await createSubscriptionFn({
        planLevel,
        paymentMethodId
      });

      setPaymentStatus("success");
      return result.data;
    } catch (err) {
      setPaymentStatus("failed");
      const errorMessage = err.message || "Failed to create subscription";
      setError(errorMessage);
      console.error("useStripe error (createSubscription):", err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Update an existing subscription to a new plan
   */
  const updateSubscription = useCallback(async (newPlanLevel) => {
    setLoading(true);
    setError(null);
    try {
      const updateSubscriptionFn = httpsCallable(functions, "updateSubscription");
      const result = await updateSubscriptionFn({
        newPlanLevel
      });

      return result.data;
    } catch (err) {
      const errorMessage = err.message || "Failed to update subscription";
      setError(errorMessage);
      console.error("useStripe error (updateSubscription):", err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Cancel a subscription
   */
  const cancelSubscription = useCallback(async (immediate = false) => {
    setLoading(true);
    setError(null);
    try {
      const cancelSubscriptionFn = httpsCallable(functions, "cancelSubscription");
      const result = await cancelSubscriptionFn({
        immediate
      });

      return result.data;
    } catch (err) {
      const errorMessage = err.message || "Failed to cancel subscription";
      setError(errorMessage);
      console.error("useStripe error (cancelSubscription):", err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Create event ticket payment
   */
  const createEventTicketPayment = useCallback(async (eventId, ticketPrice, quantity, eventName) => {
    setLoading(true);
    setError(null);
    setPaymentStatus("processing");
    try {
      const createEventTicketPaymentFn = httpsCallable(functions, "createEventTicketPayment");
      const result = await createEventTicketPaymentFn({
        eventId,
        ticketPrice,
        quantity,
        eventName: eventName || "Event Ticket Purchase"
      });

      return result.data;
    } catch (err) {
      setPaymentStatus("failed");
      const errorMessage = err.message || "Failed to create event ticket payment";
      setError(errorMessage);
      console.error("useStripe error (createEventTicketPayment):", err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Complete event ticket purchase after payment confirmation
   */
  const completeEventTicketPurchase = useCallback(async (paymentIntentId, eventId, quantity) => {
    setLoading(true);
    setError(null);
    try {
      const completeEventTicketPurchaseFn = httpsCallable(functions, "completeEventTicketPurchase");
      const result = await completeEventTicketPurchaseFn({
        paymentIntentId,
        eventId,
        quantity
      });

      setPaymentStatus("success");
      return result.data;
    } catch (err) {
      setPaymentStatus("failed");
      const errorMessage = err.message || "Failed to complete ticket purchase";
      setError(errorMessage);
      console.error("useStripe error (completeEventTicketPurchase):", err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * Clear payment status
   */
  const clearPaymentStatus = useCallback(() => {
    setPaymentStatus(null);
  }, []);

  return {
    // State
    loading,
    error,
    paymentStatus,

    // Methods
    createSetupIntent,
    savePaymentMethod,
    createSubscription,
    updateSubscription,
    cancelSubscription,
    createEventTicketPayment,
    completeEventTicketPurchase,

    // Utilities
    clearError,
    clearPaymentStatus,
  };
}
